import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowRight, Lock } from "lucide-react";
import { ASSETS } from "@/lib/assets";
import { TopBar } from "@/components/Brand";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EmailGate() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  useEffect(() => {
    const raw = typeof window !== "undefined" ? sessionStorage.getItem("clarity_result") : null;
    if (!raw) {
      navigate("/");
      return;
    }
    setResult(JSON.parse(raw));
  }, [navigate]);

  if (!result) return null;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!email || !email.includes("@")) {
      setError("Please enter a valid email.");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/leads/email`, { result_id: result.id, email, name });
      sessionStorage.setItem("clarity_email", email);
      navigate(`/result`);
    } catch (e) {
      setError("Something went wrong. You can still see your result.");
      setTimeout(() => navigate(`/result`), 1500);
    }
  };

  const skip = () => navigate(`/result`);

  return (
    <div className="min-h-screen bg-cream relative overflow-hidden">
      <TopBar />
      <main className="px-6 sm:px-10 lg:px-16 pt-6 pb-24">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-7"
          >
            <p className="font-sans text-[11px] tracking-[0.35em] uppercase text-sage-700 mb-5">
              <span className="inline-block h-px w-8 align-middle bg-sage-700/60 mr-3" />
              Your archetype is ready
            </p>
            <h1 className="font-display text-4xl sm:text-6xl tracking-tight text-ink leading-[1.05] mb-6">
              Where should we send your <em className="italic-display text-sage-700">Mind Magic Audio</em>?
            </h1>
            <p className="font-sans text-base sm:text-lg text-ink/75 leading-relaxed max-w-xl mb-10">
              Drop your email and we’ll deliver your personalized Mind Magic Audio plus a short note from Chelsea on how to use it.
              <span className="block mt-2 text-ink/55 text-sm">No spam, ever. Unsubscribe with one click.</span>
            </p>

            <form onSubmit={submit} className="space-y-4 max-w-xl" data-testid="email-gate-form">
              <div>
                <label className="block font-sans text-[10px] tracking-[0.3em] uppercase text-ink/55 mb-2">First Name <span className="opacity-50">(optional)</span></label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Chelsea"
                  className="w-full px-4 py-3.5 bg-transparent border border-cream-300 focus:border-sage-700 outline-none rounded-sm font-sans text-ink placeholder:text-ink/30"
                  data-testid="email-gate-name-input"
                />
              </div>
              <div>
                <label className="block font-sans text-[10px] tracking-[0.3em] uppercase text-ink/55 mb-2">Email Address</label>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@yourbusiness.com"
                  className="w-full px-4 py-3.5 bg-transparent border border-cream-300 focus:border-sage-700 outline-none rounded-sm font-sans text-ink placeholder:text-ink/30"
                  data-testid="email-gate-email-input"
                />
              </div>

              {error && <p className="font-sans text-sm text-red-700" data-testid="email-gate-error">{error}</p>}

              <div className="pt-3 flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
                <button
                  type="submit"
                  disabled={busy}
                  className="group inline-flex items-center justify-center gap-3 bg-ink text-cream px-7 py-4 rounded-sm font-sans text-sm tracking-[0.18em] uppercase hover:bg-sage-700 transition-colors duration-500 disabled:opacity-60"
                  data-testid="email-gate-submit"
                >
                  {busy ? "Sending…" : "Reveal My Result"}
                  {!busy && <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />}
                </button>
                <button
                  type="button"
                  onClick={skip}
                  className="font-sans text-xs tracking-[0.25em] uppercase text-ink/50 hover:text-sage-700 link-reveal"
                  data-testid="email-gate-skip"
                >
                  Skip and view result
                </button>
              </div>

              <p className="pt-4 inline-flex items-center gap-2 font-sans text-xs text-ink/45">
                <Lock className="h-3 w-3" />
                Your details are private. Powered by Chelsea Litsey · Clarity Coach.
              </p>
            </form>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.2 }}
            className="lg:col-span-5"
          >
            <div className="relative">
              <div className="relative aspect-[4/5] rounded-sm overflow-hidden bg-ink">
                <img src={ASSETS.email_gate_juggling} alt="" className="absolute inset-0 h-full w-full object-cover" />
                <div className="absolute inset-0 vignette" />
              </div>
              <div className="absolute -bottom-6 left-6 right-6 bg-cream-100 border border-sage-300/60 px-5 py-4 rounded-sm">
                <p className="font-display italic-display text-xl text-ink leading-snug">
                  “Your body has been juggling more than your mind realizes.”
                </p>
                <p className="mt-1 font-sans text-[10px] tracking-[0.3em] uppercase text-sage-700">— Chelsea</p>
              </div>
            </div>
          </motion.div>
        </div>
      </main>
    </div>
  );
}
